import { Injectable, NgZone } from '@angular/core';
import { GestureController, MenuController, Platform } from '@ionic/angular';
import { StatusBar } from '@capacitor/status-bar';
import { BehaviorSubject } from 'rxjs';
import { isWeb } from 'src/app/shared/helpers';

interface RegisteredIframe {
    id: string;
    onToggle: () => void;
}

@Injectable({
    providedIn: 'root'
})
export class FullscreenVideoService {

    private activeId: string | null = null;
    private activeContainer: HTMLElement | null = null;
    private registeredIframes: Map<Window, RegisteredIframe> = new Map();
    private orientationLockPromise: Promise<any> | null = null;

    private fullscreenIdSource = new BehaviorSubject<string | null>(null);
    fullscreenId$ = this.fullscreenIdSource.asObservable();

    constructor(
        private zone: NgZone,
        private menuCtrl: MenuController,
        private gestureCtrl: GestureController,
        private platform: Platform
    ) {
        window.addEventListener('message', this.handleWindowMessage);

        // Intercepte le retour arrière avec priorité maximale et bloque toute action si actif
        this.platform.backButton.subscribeWithPriority(99999, (processNextHandler) => {
            if (this.activeId) {
                // Ne fait rien : bloque le retour système et empêche le handler suivant de s'exécuter
                return;
            } else {
                processNextHandler();
            }
        });
    }

    private handleWindowMessage = (event: MessageEvent) => {
        if (event.data?.source === 'pray-for-all-youtube-embed' && event.data?.type === 'fullscreen-toggle') {
            const entry = this.registeredIframes.get(event.source as Window);
            if (entry) {
                this.zone.run(() => entry.onToggle());
            }
        }
    };

    registerIframe(id: string, win: Window | null | undefined, onToggle: () => void) {
        if (!win) return;
        this.registeredIframes.set(win, { id, onToggle });
    }

    unregisterIframe(win: Window | null | undefined) {
        if (!win) return;
        this.registeredIframes.delete(win);
    }

    isFullscreen(id: string): boolean {
        return this.activeId === id;
    }

    get isAnyFullscreen(): boolean {
        return this.activeId !== null;
    }

    get activeFullscreenId(): string | null {
        return this.activeId;
    }

    async toggle(id: string, containerEl?: HTMLElement | null) {
        if (this.activeId === id) {
            await this.exit();
        } else {
            await this.enter(id, containerEl);
        }
    }

    async enter(id: string, containerEl?: HTMLElement | null) {
        if (isWeb()) return;

        if (this.activeId && this.activeId !== id) {
            await this.exit();
        }

        this.activeId = id;
        this.activeContainer = containerEl || null;
        this.fullscreenIdSource.next(id);

        document.querySelectorAll('ion-header').forEach((header: any) => {
            header.style.setProperty('display', 'none', 'important');
        });
        document.querySelectorAll('ion-tab-bar').forEach((tabBar: any) => {
            tabBar.style.setProperty('display', 'none', 'important');
        });
        document.querySelectorAll('.top-header-content').forEach((el: any) => {
            el.style.setProperty('display', 'none', 'important');
        });

        document.body.style.overflow = 'hidden';
        document.querySelectorAll('ion-content').forEach((content: any) => {
            content.scrollToTop(0);
            content.style.setProperty('--overflow', 'hidden');
        });

        try {
            if (screen.orientation && 'lock' in screen.orientation) {
                this.orientationLockPromise = (screen.orientation as any).lock('landscape');
                await this.orientationLockPromise;
            }
        } catch {
            this.orientationLockPromise = null;
        }

        StatusBar.hide().catch(() => { });
        document.documentElement.style.backgroundColor = '#000000';
        document.body.style.backgroundColor = '#000000';

        // Désactive les gestes de glissement de menu et de navigation Ionic
        this.menuCtrl.swipeGesture(false);
        document.querySelectorAll('ion-router-outlet').forEach((ro: any) => ro.swipeGesture = false);
    }

    async exit() {
        if (!this.activeId) return;

        const container = this.activeContainer;
        this.activeId = null;
        this.activeContainer = null;

        if (this.orientationLockPromise) {
            try {
                await this.orientationLockPromise;
            } catch { }
            this.orientationLockPromise = null;
        }

        try {
            screen.orientation?.unlock?.();
        } catch { }

        if (container) {
            container.classList.remove('fullscreen-sim');
            container.style.removeProperty('transform');
        }

        document.querySelectorAll('ion-header').forEach((header: any) => {
            header.style.setProperty('display', 'block', 'important');
        });
        document.querySelectorAll('ion-tab-bar').forEach((tabBar: any) => {
            tabBar.style.setProperty('display', 'flex', 'important');
        });
        document.querySelectorAll('.top-header-content').forEach((el: any) => {
            el.style.setProperty('display', '', 'important');
        });

        document.body.style.overflow = '';
        document.querySelectorAll('ion-content').forEach((content: any) => {
            content.style.removeProperty('--overflow');
        });

        StatusBar.show().catch(() => { });
        document.documentElement.style.backgroundColor = '';
        document.body.style.backgroundColor = '';

        this.menuCtrl.swipeGesture(true);
        document.querySelectorAll('ion-router-outlet').forEach((ro: any) => ro.swipeGesture = true);

        this.fullscreenIdSource.next(null);
    }

    destroy(id?: string) {
        if (id && this.activeId !== id) return;
        if (this.activeId) {
            this.exit();
        }
    }
}